import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Grid,
  Tooltip,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Speed as SpeedIcon,
} from "@mui/icons-material";
import { TranslationJob } from "../services/TranslationService";

interface JobProgressEstimatorProps {
  jobs: TranslationJob[];
  averageTimePerChunkMs?: number; // Estimated time per chunk in ms, default to 5000 (5s)
}

const JobProgressEstimator: React.FC<JobProgressEstimatorProps> = ({
  jobs,
  averageTimePerChunkMs = 5000,
}) => {
  // Filter for active jobs
  const activeJobs = useMemo(
    () =>
      jobs.filter(
        (job) => job.status === "in-progress" || job.status === "queued"
      ),
    [jobs]
  );

  // Calculate stats
  const stats = useMemo(() => {
    if (activeJobs.length === 0) return null;

    let totalChunks = 0;
    let completedChunks = 0;

    activeJobs.forEach((job) => {
      totalChunks += job.totalChunks || 0;
      completedChunks += job.completedChunks || 0;
    });

    const remainingChunks = totalChunks - completedChunks;
    const progress =
      totalChunks > 0 ? (completedChunks / totalChunks) * 100 : 0;

    // Estimate remaining time
    const estimatedRemainingTimeMs = remainingChunks * averageTimePerChunkMs;

    return {
      totalChunks,
      completedChunks,
      remainingChunks,
      progress,
      estimatedRemainingTimeMs,
      activeJobCount: activeJobs.length,
    };
  }, [activeJobs, averageTimePerChunkMs]);

  if (!stats || stats.activeJobCount === 0) {
    return null;
  }

  // Format time duration
  const formatDuration = (ms: number): string => {
    if (ms < 60000) {
      return "< 1 min";
    }
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    }

    return `${minutes} min`;
  };

  // Calculate ETA time
  const getEtaTime = (ms: number): string => {
    const eta = new Date(Date.now() + ms);
    return eta.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 4,
        bgcolor: "background.paper",
        borderLeft: "4px solid",
        borderColor: "primary.main",
      }}
    >
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <SpeedIcon sx={{ mr: 1, color: "primary.main" }} />
          Processing Status
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {stats.activeJobCount} active job
          {stats.activeJobCount !== 1 ? "s" : ""}
        </Typography>
      </Box>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={8}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Box sx={{ width: "100%", mr: 1 }}>
              <LinearProgress variant="determinate" value={stats.progress} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {Math.round(stats.progress)}%
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Processed {stats.completedChunks} of {stats.totalChunks} chunks
            across all active jobs
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Tooltip
              title={`Estimated completion time: ${getEtaTime(
                stats.estimatedRemainingTimeMs
              )}`}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AccessTimeIcon
                  fontSize="small"
                  sx={{ mr: 0.5, color: "text.secondary" }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  ~ {formatDuration(stats.estimatedRemainingTimeMs)}
                </Typography>
              </Box>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              Estimated wait time
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default JobProgressEstimator;
